import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCareCreditIntegrationV1Service, serviceName } from '../service';
import { GetPurchasePromosIO, ServiceQueries } from '../types';
import { useCareCreditIntegrationV1Queries } from './use-care-credit-integration-v1-queries';
import {
  UseCareCreditIntegrationV1QueryEndpointArgs,
  useCareCreditIntegrationV1Query,
} from './use-care-credit-integration-v1-query';

/**
 * A hook that returns a query for the `GetPurchasePromos` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPurchasePromosQuery = <E = unknown, D = GetPurchasePromosIO['output']>(
  args: UseCareCreditIntegrationV1QueryEndpointArgs<'GetPurchasePromos', E, D>
) =>
  useCareCreditIntegrationV1Query({
    endpointName: 'GetPurchasePromos',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetPurchasePromos` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPurchasePromosQueries = <E = unknown, D = GetPurchasePromosIO['output']>(
  argsArr: UseCareCreditIntegrationV1QueryEndpointArgs<'GetPurchasePromos', E, D>[]
) =>
  useCareCreditIntegrationV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetPurchasePromos',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetPurchasePromos` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetPurchasePromos = () => {
  const queryClient = useQueryClient();

  const fetchGetPurchasePromos = async <E = unknown, D = GetPurchasePromosIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCareCreditIntegrationV1QueryEndpointArgs<'GetPurchasePromos', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetPurchasePromosIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetPurchasePromos'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetPurchasePromosIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetPurchasePromos'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetPurchasePromos'>({
          serviceName,
          endpointName: 'GetPurchasePromos',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCareCreditIntegrationV1Service.GetPurchasePromos(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetPurchasePromos;
};
